import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Button,Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import Layout from '../../layout/layout';
import { useToast } from "../../components/ToastContext";
import { api } from '../../services/api';
import RefreshIcon from "@mui/icons-material/Refresh";

const AdminLambda = () => {
  const theme = useTheme();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  //  Initialize loadingStart FROM localStorage on mount / navigation back
  const [loadingStart, setLoadingStart] = useState(() => {
    try {
      const saved = localStorage.getItem('lambdaLoadingState');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  //  Set TRUE - save to localStorage (persist across navigation)
  const setStarting = (functionName) => {
    setLoadingStart(prev => {
      const updated = { ...prev, [functionName]: true };
      localStorage.setItem('lambdaLoadingState', JSON.stringify(updated));
      return updated;
    });
  };

  //  Set FALSE - remove from localStorage (reset button to "Start")
  const clearStarting = (functionName) => {
    setLoadingStart(prev => {
      const updated = { ...prev };
      delete updated[functionName]; //  Remove key completely

      if (Object.keys(updated).length === 0) {
        localStorage.removeItem('lambdaLoadingState'); //  Clean up if empty
      } else {
        localStorage.setItem('lambdaLoadingState', JSON.stringify(updated));
      }
      return updated;
    });
  };

  //  Handle Start Lambda
  const handleStart = async (functionName) => {
    if (loadingStart[functionName]) return; //  Prevent double click

    setStarting(functionName); //  Button → "Starting..." + saved to localStorage

    try {
      await api.post("/api/AwsLambda/batch-invoke", {
        invocations: [
          {
            functionName,
            payload: JSON.stringify({ action: "start" })
          }
        ]
      });

      toast.success(`Started Lambda: ${functionName}`);
      clearStarting(functionName); //  SUCCESS → Button back to "Start" + localStorage cleared

    } catch (error) {
      toast.error(error.message || `Failed to start Lambda: ${functionName}`);
      clearStarting(functionName); //  ERROR → Button back to "Start" + localStorage cleared
    }
  };

  //  Columns
  const columns = [
   
    {
      field: "functionName",
      headerName: "Function Name",
      fontWeight: "bold",
      flex: 2
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        const starting = !!loadingStart[params.row.functionName]; //  Read from state

        return (
          <Box
            component="span"
              sx={{
                      bgcolor: '#e6f4ea',
                      color: '#2e7d32',
                      border: '1px solid #2e7d32',
                      borderRadius: '4px',
                      padding: '4px 16px',
                      marginRight: '8px',
                      // marginLeft: '18px',
                      gap:20,
                      cursor: 'pointer',
                          alignItems: 'center',
                          
    opacity: loadingStart[params.row.functionName] ? 0.6 : 1,
    pointerEvents: loadingStart[params.row.functionName] ? 'none' : 'auto',
                    }}
            onClick={() => handleStart(params.row.functionName)}
          >
            {starting ? (
              <>
                <CircularProgress size={12} color="warning" />
                <span>Executing ...</span>
              </>
            ) : (
              <span>Start</span>
            )}
          </Box>
        );
      }
    }
  ];

  //  Fetch Lambda Functions
  const fetchLambdaFunctions = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/AwsLambda/functions");

      const formatted = (response?.data || []).map((fn, index) => ({
        id: index,
        functionName: fn
      }));

      setRows(formatted);
    } catch (error) {
      toast.error(error.message || "Failed to fetch AWS Lambda functions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLambdaFunctions();
  }, []);

  return (
    <Layout pageType="sideBar">
       <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          <Tooltip title="Refresh records" arrow>

  <Button
    variant="outlined"
    onClick={fetchLambdaFunctions}
    disabled={loading}
    sx={{
      width: 30,
      height: 30,
      minWidth: 30,
      minHeight: 30,
      padding: 0,     
      borderRadius: 1,    
      color: "#2e7d32",
      borderColor: "#2e7d32",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      "& .MuiButton-startIcon": {
        margin: 0,        
      },

      "&:hover": {
        borderColor: "#1b5e20",
        backgroundColor: "#00a00b",
        color: 'white',
      },
    }}
  >
    {loading ? (
      <CircularProgress size={14} />
    ) : (
      <RefreshIcon fontSize="small" />
    )}
  </Button>
  </Tooltip> 
        </Box>
      <Paper
        sx={{
          p: theme.spacing(3),
          bgcolor: theme.palette.mode === 'dark' ? '#212330' : '#fff',
          mt: theme.spacing(2),
          boxShadow: theme.shadows[3],
        }}
      >
       
        <Box sx={{ flex: 1,  }}>
          <Box sx={{ height: 'auto', width: '100%' }}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={22} />
                <Typography variant="body2">
                  Loading AWS Lambda functions…
                </Typography>
              </Box>
            ) : (
             
             <DataGrid
              rows={rows}
              columns={columns}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 20]}
              // getRowId={(row) => row.userId}
              disableSelectionOnClick
              hideFooterSelectedRowCount 
               sx={{
                '& .MuiDataGrid-root': {
              bgcolor: theme.palette.mode === 'dark' ? '#212330' : '#ffffff', // Adjust background color based on theme
            },
                '& .MuiDataGrid-filler': {
      display: 'none',
    },
                bgcolor: theme.palette.mode === 'dark' ? '#212330' : '#fff', // Use black for dark mode and white for light mode
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: theme.palette.mode === 'dark' ? '#212330' : '#2b61aa', // Adjust header background color
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold !important',
    
                },
                '& .MuiDataGrid-cell': {
                  color: theme.palette.mode === 'dark' ? '#ffffff' : '#212330', // Adjust text color based on theme
                  borderBottomWidth: '1px solid',
                  borderColor:theme.palette.mode === 'dark' ? '#212330' : '#2b61aa',
           
                },
                '& .MuiDataGrid-row:nth-of-type(even)': {
                  bgcolor: theme.palette.mode === 'dark' ? '#2a2d3c' : '#fafafa', // Adjust even row background color
                },
                '& .MuiDataGrid-row:nth-of-type(odd)': {
                  bgcolor: theme.palette.mode === 'dark' ? '#212330' : '#ffffff', // Adjust odd row background color
                },
                '& .MuiDataGrid-footerContainer': {
                  bgcolor: theme.palette.mode === 'dark' ? '#2a2d3c' : '#f0f0f0', // Adjust footer background color
                },
              }}
            />





            )}
          </Box>
        </Box>
      </Paper>
    </Layout>
  );
};

export default AdminLambda;



